import { Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import { wrapCreateBrowserRouter } from '@sentry/react';
import { DevelopmentOnly } from 'components/auth/DevelopmentOnly';
import { RequirePermission } from 'components/auth/RequirePermission';
import ErrorFallback from 'components/fallback/ErrorFallback';
import { MainLayout } from 'components/layout/MainLayout';
import { SidebarLayout } from 'components/layout/SidebarLayout';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { alertsRoutes } from 'pages/Alerts/Alerts.routes';
import { AssetsLayout } from 'pages/Assets/Assets.layout';
import { assetsRoutes } from 'pages/Assets/Assets.routes';
import { assetsOverviewRoutes } from 'pages/Assets/AssetsOverview.routes';
import {
  blueprintsOverviewRoutes,
  blueprintsRoutes,
  editBlueprintRoute,
  newBlueprintRoute,
} from 'pages/Blueprints/Blueprints.routes';
import { managementRoutes } from 'pages/Management/Management.routes';
import { newProjectRoute, projectsOverviewRoutes, projectsRoutes } from 'pages/Projects/Projects.routes';
import { sharedBlueprintRoutes } from 'pages/Shared/Shared.routes';
import { Component as TestDataGenerator } from 'pages/TestDataGenerator';
import { USER_PREFERENCES } from 'utils/constants';

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

const DefaultRedirect = () => {
  const location = useLocation();
  const [landingPage] = useLocalStorage(
    USER_PREFERENCES.LANDING_PAGE.LOCAL_STORAGE_KEY,
    USER_PREFERENCES.LANDING_PAGE.DEFAULT
  );

  const isAccessDenied = !!location.state?.accessDenied;

  return <Navigate to={isAccessDenied ? USER_PREFERENCES.LANDING_PAGE.DEFAULT : landingPage} replace />;
};

export const routes = [
  {
    path: '/login',
    lazy: () => import('./pages/Login'),
  },
  ...sharedBlueprintRoutes,
  {
    element: <MainLayout />,
    path: '/',
    errorElement: <ErrorFallback />,
    children: [
      {
        index: true,
        element: <DefaultRedirect />,
      },
      ...projectsOverviewRoutes,
      {
        ...assetsOverviewRoutes,
        element: <AssetsLayout routes={[assetsOverviewRoutes]} />,
      },
      ...alertsRoutes,
      ...blueprintsOverviewRoutes,
      ...blueprintsRoutes,
      newProjectRoute,
      newBlueprintRoute,
      editBlueprintRoute,
      {
        path: 'glossary',
        lazy: () => import('./pages/Glossary'),
      },
      {
        path: 'preferences',
        lazy: () => import('./pages/Preferences'),
      },
      {
        path: '',
        element: <SidebarLayout routes={[...projectsRoutes, ...managementRoutes, ...assetsRoutes]} />,
        children: [...projectsRoutes, ...managementRoutes, ...assetsRoutes],
      },
      {
        path: '/404',
        lazy: () => import('./pages/NotFound'),
      },
      {
        path: '/test-data',
        element: (
          <DevelopmentOnly>
            <RequirePermission permission="greenerUnit:createTestData">
              <TestDataGenerator />
            </RequirePermission>
          </DevelopmentOnly>
        ),
      },
      {
        path: '*',
        element: <DefaultRedirect />,
      },
    ],
  },
];

export const browserRouter = sentryCreateBrowserRouter(routes);
